import React from 'react';
import { m } from 'motion/react';
import { CheckCircle2, ChevronRight, Calculator, ShieldCheck, Receipt, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Pricing: React.FC = () => {
  const { t } = useLanguage();
  const pricingT = t.pricing;

  const packages = [
    { 
      key: 'starter',
      data: pricingT.starter,
      highlight: false
    },
    { 
      key: 'standard',
      data: pricingT.standard,
      highlight: true
    },
    { 
      key: 'pro',
      data: pricingT.pro,
      highlight: false
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-pstax-bg overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-display font-bold mb-4">{pricingT.title}</h2>
          <p className="text-pstax-gray max-w-2xl mx-auto">
            {pricingT.sub}
          </p>
        </div>

        {/* Value/Benefit Box */}
        <m.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mb-12 bg-white rounded-[40px] border border-pstax-blue/20 shadow-xl overflow-hidden"
        >
          <div className="p-10 lg:p-14">
            <div className="flex flex-col lg:flex-row gap-12">
              <div className="lg:w-2/5 flex flex-col justify-center">
                <m.h3 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                  className="text-3xl font-display font-bold text-pstax-navy mb-6 leading-tight"
                  dangerouslySetInnerHTML={{ __html: pricingT.valueBox.headline }}
                />
                <div className="space-y-4 text-pstax-gray text-base leading-relaxed">
                  <p dangerouslySetInnerHTML={{ __html: pricingT.valueBox.subheadline }} />
                  <div className="pt-2 font-bold text-pstax-navy text-lg leading-snug">
                    {pricingT.valueBox.trustedText}
                  </div>
                </div>
                
                <div className="mt-8">
                  <a 
                    href="#fit-check" 
                    className="inline-flex py-4 px-8 rounded-full bg-pstax-blue text-pstax-navy font-bold items-center gap-2 hover:bg-pstax-navy hover:text-white transition-all group shadow-lg shadow-pstax-blue/10"
                    data-cta-location="pricing"
                  >
                    {pricingT.valueBox.cta}
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>

              <div className="lg:w-3/5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pricingT.valueBox.items.map((item: any, idx: number) => (
                  <m.div 
                    key={idx}
                    whileHover={{ y: -5, backgroundColor: "#fff", boxShadow: "0 10px 30px -10px rgba(0, 114, 206, 0.15)" }}
                    className="p-6 rounded-2xl bg-pstax-blue/5 border border-pstax-blue/10 transition-all cursor-default"
                  >
                    {idx === 0 && <Sparkles size={22} className="text-pstax-blue mb-3" />}
                    {idx === 1 && <ShieldCheck size={22} className="text-pstax-blue mb-3" />}
                    {idx === 2 && <Calculator size={22} className="text-pstax-blue mb-3" />}
                    {idx === 3 && <Receipt size={22} className="text-pstax-blue mb-3" />}
                    
                    <h4 className="font-bold text-pstax-navy mb-2">{item.title}</h4>
                    <p className="text-[13px] text-pstax-gray leading-normal" dangerouslySetInnerHTML={{ __html: item.desc }} />
                  </m.div>
                ))}
              </div>
            </div>
          </div>
          <div className="px-10 py-4 bg-pstax-blue/5 border-t border-pstax-blue/10 text-[11px] text-pstax-gray italic text-center lg:text-left">
            {pricingT.valueBox.disclaimer}
          </div>
        </m.div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packages.map((pkg: any) => (
            <m.div
              key={pkg.key}
              whileHover={{ y: -10 }}
              className={`p-8 rounded-[40px] border transition-all flex flex-col relative ${
                pkg.highlight 
                  ? 'bg-pstax-navy text-white border-pstax-navy lg:scale-105 shadow-2xl z-10' 
                  : 'bg-white border-pstax-border shadow-sm'
              }`}
            >
              {pkg.highlight && (
                <div className="absolute -top-3 left-0 right-0 mx-auto w-fit max-w-[90%] bg-pstax-blue text-pstax-navy text-[10px] sm:text-xs font-black uppercase px-4 py-1.5 rounded-full shadow-lg z-20 text-center leading-tight">
                  {pkg.data.effectiveLabel}
                </div>
              )}
              
              <div className="mb-8">
                <h3 className={`text-xl font-bold mb-2 ${pkg.highlight ? 'text-pstax-blue' : 'text-pstax-navy'}`}>
                  {pkg.data.name}
                </h3>
                <div className="flex items-end gap-1 mb-4">
                  <span className="text-4xl font-display font-bold">€ {pkg.data.price}</span>
                  <span className={`${pkg.highlight ? 'text-white/60' : 'text-pstax-gray'} text-sm mb-1`}>{pricingT.interval}</span>
                </div>
                <p className={`${pkg.highlight ? 'text-white/70' : 'text-pstax-gray'} text-sm`}>
                  {pkg.data.desc}
                </p>
              </div>

              <div className="flex-1 space-y-4 mb-8">
                {pkg.data.features.map((feature: string, i: number) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 size={18} className="shrink-0 text-pstax-blue" />
                    <span className="text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              <a 
                href="#fit-check" 
                className={`w-full py-4 rounded-full font-bold text-center flex items-center justify-center gap-2 group transition-all ${
                  pkg.highlight 
                    ? 'bg-pstax-blue text-pstax-navy hover:bg-white' 
                    : 'bg-pstax-navy text-white hover:bg-pstax-navy/90'
                }`}
                aria-label={`Check if the ${pkg.data.name} package is the right fit for you`}
                data-cta-location="pricing"
              >
                {pricingT.valueBox.cta}
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </m.div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-[12px] text-pstax-gray italic max-w-2xl mx-auto leading-relaxed">
            {pricingT.note}
          </p>
        </div>
      </div>
    </section>
  );
};
